import { useMutation, useQuery } from '@tanstack/react-query'
import { useEffect, useMemo, useRef, useState } from 'react'
import {
  Button,
  DateRangePicker,
  Field,
  Input,
  Popover,
  Select,
  StateBlock,
  useToast,
} from '@w7/ui'
import { useCmMe } from '@/api/hooks/useCmMe'
import { surveillanceClient, USE_STATIC_SURVEILLANCE } from '@/api/surveillanceClient'
import {
  canClearSurveillanceBetaMembers,
  canClearSurveillanceNewMembers,
  canGenerateSurveillanceBetaGraph,
  canGenerateSurveillanceNewGraph,
} from '@/app/staffAccess'
import { CmPanel } from '@/features/catalog/CmPanel'
import { ll } from '@/lib/format'
import { RunnerOddsChart } from './RunnerOddsChart'
import { SurveillanceMemberSearch } from './SurveillanceMemberSearch'
import {
  APPLY_GRAPH_VIEW_OPTIONS,
  GENERATE_GRAPH_OPTIONS,
  applyGraphViewTypeToRunners,
  defaultGraphViewType,
  type SurveillanceGraphViewType,
} from './graphViewType'
import {
  CURRENCY_OPTIONS,
  SURVEILLANCE_DATE_PRESETS,
  SURVEILLANCE_RANGE_MAX_DAYS,
  canSearchSurveillance,
  emptyGraphFilter,
  surveillanceDatePresetOf,
  surveillanceModeForRange,
  validateStakeRange,
} from './surveillanceFilters'
import { formatSurveillanceWinnerLabel } from './surveillanceLabels'
import type { SurveillanceGraph, SurveillanceGraphFilter } from './types'
import './surveillance.css'

function llDay(iso: string): string {
  const [y, m, d] = iso.split('-').map(Number)
  return y && m && d ? ll(new Date(y, m - 1, d)) : ll(iso)
}

function rangeSummary(from: string, to: string): string {
  if (!from || !to) return ''
  return from === to ? llDay(from) : `${llDay(from)} – ${llDay(to)}`
}

function parseOptionalNumber(raw: string): number | null {
  const t = raw.trim()
  if (!t) return null
  const n = Number(t)
  return Number.isFinite(n) ? n : null
}

/** Merged Surveillance — Postgres within 7d ending today, else BQ (Bet Ticker Previous switch). */
export function SurveillanceWorkspace() {
  const toast = useToast()
  const me = useCmMe()
  const canClearMembers =
    canClearSurveillanceNewMembers(me.data?.caller) ||
    canClearSurveillanceBetaMembers(me.data?.caller)
  const canGenerateGraph =
    canGenerateSurveillanceNewGraph(me.data?.caller) ||
    canGenerateSurveillanceBetaGraph(me.data?.caller)
  const [draft, setDraft] = useState(() => emptyGraphFilter())
  const mode = draft.mode
  const isArchive = mode === 'archive'
  const [graph, setGraph] = useState<SurveillanceGraph | null | undefined>(undefined)
  const [searched, setSearched] = useState(false)
  const [viewGraphType, setViewGraphType] = useState<SurveillanceGraphViewType>(() =>
    defaultGraphViewType(emptyGraphFilter().mode),
  )
  const [chartEpoch, setChartEpoch] = useState(0)
  const [generateOpen, setGenerateOpen] = useState(false)
  const generateAnchor = useRef<HTMLButtonElement>(null)

  const subGamesQ = useQuery({
    queryKey: [
      'cm',
      'surveillance',
      'sub-games',
      mode,
      isArchive ? (draft.fromDate ?? '') : '',
      isArchive ? (draft.toDate ?? '') : '',
    ],
    queryFn: () =>
      surveillanceClient.listSubGames(
        mode,
        isArchive
          ? {
              fromDate: draft.fromDate || undefined,
              toDate: draft.toDate || undefined,
            }
          : undefined,
      ),
  })

  const selectedSubGame = useMemo(
    () => subGamesQ.data?.subGames.find((s) => s.name === draft.eventType) ?? null,
    [subGamesQ.data, draft.eventType],
  )

  const eventsQ = useQuery({
    queryKey: [
      'cm',
      'surveillance',
      'events',
      mode,
      selectedSubGame?.id ?? '',
      selectedSubGame?.name ?? '',
      draft.fromDate ?? '',
      draft.toDate ?? '',
      draft.playerNames.join(','),
      draft.fromStake ?? '',
      draft.toStake ?? '',
      draft.currencyType,
    ],
    enabled: Boolean(selectedSubGame?.id && selectedSubGame?.name),
    queryFn: () =>
      surveillanceClient.listEvents({
        mode,
        subGameId: selectedSubGame!.id,
        subGameName: selectedSubGame!.name,
        fromDate: draft.fromDate || undefined,
        toDate: draft.toDate || undefined,
        playerNames: draft.playerNames,
        fromStake: draft.fromStake,
        toStake: draft.toStake,
        currencyType: draft.currencyType,
      }),
  })

  const marketsQ = useQuery({
    queryKey: [
      'cm',
      'surveillance',
      'markets',
      mode,
      draft.eventId,
      draft.fromDate ?? '',
      draft.toDate ?? '',
      selectedSubGame?.name ?? '',
    ],
    enabled: Boolean(draft.eventId),
    queryFn: () =>
      surveillanceClient.listMarkets({
        mode,
        eventId: draft.eventId,
        fromDate: draft.fromDate || undefined,
        toDate: draft.toDate || undefined,
        subGameName: selectedSubGame?.name,
      }),
  })

  // Surface picker/API errors (API message or client static fallback).
  useEffect(() => {
    if (subGamesQ.isError && subGamesQ.error instanceof Error) {
      toast.error(subGamesQ.error.message)
    }
  }, [subGamesQ.isError, subGamesQ.error, toast])

  useEffect(() => {
    if (eventsQ.isError && eventsQ.error instanceof Error) {
      toast.error(eventsQ.error.message)
    }
  }, [eventsQ.isError, eventsQ.error, toast])

  useEffect(() => {
    if (marketsQ.isError && marketsQ.error instanceof Error) {
      toast.error(marketsQ.error.message)
    }
  }, [marketsQ.isError, marketsQ.error, toast])

  // If date range drops the selected event, clear cascade.
  useEffect(() => {
    if (!draft.eventId || !eventsQ.data) return
    const stillThere = eventsQ.data.events.some((e) => e.id === draft.eventId)
    if (!stillThere) {
      setDraft((prev) => ({ ...prev, eventId: '', marketId: '' }))
      setGraph(undefined)
      setSearched(false)
    }
  }, [eventsQ.data, draft.eventId])

  const search = useMutation({
    mutationFn: (filter: SurveillanceGraphFilter) =>
      surveillanceClient.getGraph(filter),
    onSuccess: (res) => {
      setSearched(true)
      if (!res.ok) {
        setGraph(null)
        toast.error(res.message)
        return
      }
      // Lab: search resets view type (live → Full / archive → Inplay) then
      // beta also rewrites runner rate URLs via changeGraphTypeView.
      const nextView = defaultGraphViewType(mode)
      setViewGraphType(nextView)
      setGraph({
        ...res.data,
        h: applyGraphViewTypeToRunners(res.data.h, nextView),
      })
      setChartEpoch((n) => n + 1)
      const betCount = res.data.h.reduce((n, r) => n + r.b.length, 0)
      if (!res.data.h.length) {
        toast.info('No runner data for this market')
      } else if (betCount === 0) {
        toast.info('Market loaded — no bets match the current filters')
      }
    },
    onError: (err: Error) => toast.error(err.message),
  })

  const regenerate = useMutation({
    mutationFn: (graphType: SurveillanceGraphViewType) =>
      surveillanceClient.regenerateGraph(draft.marketId, graphType, draft.eventId),
    onSuccess: (res) => toast.success(res.message),
    onError: (err: Error) => toast.error(err.message),
  })

  const changeGraphTypeView = (next: SurveillanceGraphViewType) => {
    setViewGraphType(next)
    setGraph((prev) => {
      if (!prev) return prev
      return { ...prev, h: applyGraphViewTypeToRunners(prev.h, next) }
    })
    // Lab remounts high-stock after URL rewrite so rates re-fetch.
    setChartEpoch((n) => n + 1)
  }

  const subGameOptions = useMemo(
    () => [
      { value: '', label: 'Select sub game' },
      ...(subGamesQ.data?.subGames ?? []).map((s) => ({
        value: s.name,
        label: s.name,
      })),
    ],
    [subGamesQ.data],
  )

  const eventOptions = useMemo(
    () => [
      { value: '', label: eventsQ.isFetching ? 'Loading events…' : 'Select event' },
      ...(eventsQ.data?.events ?? []).map((e) => ({
        value: e.id,
        label: e.openDate
          ? `${e.name} · ${new Date(e.openDate).toLocaleDateString()}`
          : e.name,
      })),
    ],
    [eventsQ.data, eventsQ.isFetching],
  )

  const marketOptions = useMemo(
    () => [
      { value: '', label: marketsQ.isFetching ? 'Loading markets…' : 'Select market' },
      ...(marketsQ.data?.markets ?? []).map((m) => ({
        value: m.betfairId,
        label: m.name,
      })),
    ],
    [marketsQ.data, marketsQ.isFetching],
  )

  const currencyOptions = CURRENCY_OPTIONS.map((c) => ({ value: c, label: c }))

  const canSearch = canSearchSurveillance(draft)

  const patch = (partial: Partial<SurveillanceGraphFilter>) => {
    setDraft((prev) => ({ ...prev, ...partial }))
  }

  const resetResults = () => {
    setGraph(undefined)
    setSearched(false)
  }

  const applyDateRange = (from: string, to: string) => {
    const nextMode = surveillanceModeForRange(from, to)
    setDraft((prev) => ({
      ...prev,
      fromDate: from,
      toDate: to,
      mode: nextMode,
      // Picker catalogs differ between Postgres and BQ — reset cascade.
      eventType: '',
      eventId: '',
      marketId: '',
    }))
    setViewGraphType(defaultGraphViewType(nextMode))
    resetResults()
  }

  const onSearch = () => {
    const stakeErr = validateStakeRange(draft.fromStake, draft.toStake)
    if (stakeErr) {
      toast.error(stakeErr)
      return
    }
    if (!canSearch) {
      toast.error('Select sub game, event and market')
      return
    }
    if (!draft.fromDate || !draft.toDate) {
      toast.error('Select a date range')
      return
    }
    if (draft.toDate < draft.fromDate) {
      toast.error('To date must be on or after From date')
      return
    }
    search.mutate({ ...draft, mode })
  }

  const addMember = (name: string) => {
    const n = name.trim()
    if (!n) return
    if (draft.playerNames.includes(n)) return
    patch({ playerNames: [...draft.playerNames, n] })
  }

  const removeMember = (name: string) => {
    patch({ playerNames: draft.playerNames.filter((p) => p !== name) })
  }

  return (
    <CmPanel
      title="Surveillance"
      subtitle="Settled exchange bets on odds timeline — Postgres ≤7 days ending today, else BigQuery (Bet Ticker Previous switch)"
      wide
      actions={
        USE_STATIC_SURVEILLANCE ? (
          <span className="cm-surv-badge" title="VITE_USE_STATIC_SURVEILLANCE=true">
            Static demo data
          </span>
        ) : (
          <span
            className="cm-surv-badge"
            title={
              isArchive
                ? 'BQ: bqGetSubGamesForSurv / bqGetEventsForBetTickerSurv / bqGetMarketsForSurv / bqGetBetsForSurv'
                : 'Live: getSubGames / getEvents / getMarkets / getBetsForSurveilliance'
            }
          >
            {isArchive ? 'BQ CM-API' : 'Live CM-API'}
          </span>
        )
      }
    >
      <div className="cm-surv-stack">
        <section
          className="cm-catalog-card cm-surv-filters cm-surv-filters-compact"
          aria-label="Filters"
        >
          {/* 1 — Members + market cascade */}
          <div className="cm-surv-row cm-surv-row-primary">
            <SurveillanceMemberSearch
              selected={draft.playerNames}
              onAdd={addMember}
            />
            <Field label="Sub game" required className="cm-surv-field-market">
              {({ id }) => (
                <Select
                  id={id}
                  value={draft.eventType}
                  options={subGameOptions}
                  onChange={(e) => {
                    patch({
                      eventType: e.target.value,
                      eventId: '',
                      marketId: '',
                    })
                    resetResults()
                  }}
                />
              )}
            </Field>
            <Field label="Event" required className="cm-surv-field-market">
              {({ id }) => (
                <Select
                  id={id}
                  value={draft.eventId}
                  options={eventOptions}
                  disabled={!selectedSubGame}
                  onChange={(e) => {
                    patch({ eventId: e.target.value, marketId: '' })
                    resetResults()
                  }}
                />
              )}
            </Field>
            <Field label="Market" required className="cm-surv-field-market">
              {({ id }) => (
                <Select
                  id={id}
                  value={draft.marketId}
                  options={marketOptions}
                  disabled={!draft.eventId}
                  onChange={(e) => {
                    patch({ marketId: e.target.value })
                    resetResults()
                  }}
                />
              )}
            </Field>
          </div>
          {draft.playerNames.length ? (
            <div className="cm-surv-selected" aria-label="Selected members">
              <span className="cm-surv-selected-count">
                {draft.playerNames.length} selected
              </span>
              <div className="cm-surv-selected-scroll">
                {draft.playerNames.map((n) => (
                  <button
                    key={n}
                    type="button"
                    className="cm-surv-chip"
                    onClick={() => removeMember(n)}
                    title={`Remove ${n}`}
                    aria-label={`Remove ${n}`}
                  >
                    <span className="cm-surv-chip-name">{n}</span>
                    <span className="cm-surv-chip-x" aria-hidden>
                      ×
                    </span>
                  </button>
                ))}
              </div>
              {canClearMembers ? (
                <Button
                  type="button"
                  variant="secondary"
                  size="sm"
                  className="cm-surv-btn"
                  icon="close"
                  onClick={() => patch({ playerNames: [] })}
                >
                  Clear members
                </Button>
              ) : null}
            </div>
          ) : null}
          {selectedSubGame && eventsQ.isSuccess && eventsQ.data.events.length === 0 ? (
            <p className="cm-surv-inline-warn">
              No events in this range — widen the date range.
            </p>
          ) : null}

          {/* 2 — Dates + stake + Search */}
          <div className="cm-surv-row cm-surv-row-tools">
            {draft.fromDate && draft.toDate ? (
              <div className="cm-surv-field-range">
                <DateRangePicker
                  label="Date range"
                  preset={surveillanceDatePresetOf(draft.fromDate, draft.toDate)}
                  value={{ from: draft.fromDate, to: draft.toDate }}
                  summary={rangeSummary(draft.fromDate, draft.toDate)}
                  presets={SURVEILLANCE_DATE_PRESETS}
                  maxSpanDays={SURVEILLANCE_RANGE_MAX_DAYS}
                  onChange={(next) => applyDateRange(next.from, next.to)}
                />
              </div>
            ) : null}
            <Field label="From stake" className="cm-surv-field-stake">
              {({ id, describedBy }) => (
                <Input
                  id={id}
                  aria-describedby={describedBy}
                  type="number"
                  min={0}
                  placeholder="Min"
                  value={draft.fromStake ?? ''}
                  onChange={(e) =>
                    patch({ fromStake: parseOptionalNumber(e.target.value) })
                  }
                />
              )}
            </Field>
            <Field label="To stake" className="cm-surv-field-stake">
              {({ id, describedBy }) => (
                <Input
                  id={id}
                  aria-describedby={describedBy}
                  type="number"
                  min={0}
                  placeholder="Max"
                  value={draft.toStake ?? ''}
                  onChange={(e) =>
                    patch({ toStake: parseOptionalNumber(e.target.value) })
                  }
                />
              )}
            </Field>
            <Field label="Currency" className="cm-surv-field-stake">
              {({ id }) => (
                <Select
                  id={id}
                  value={draft.currencyType}
                  options={currencyOptions}
                  onChange={(e) =>
                    patch({
                      currencyType: e.target
                        .value as SurveillanceGraphFilter['currencyType'],
                    })
                  }
                />
              )}
            </Field>
            <Field label={'\u00a0'} className="cm-surv-field-action cm-surv-field-action-end">
              {() => (
                <div className="cm-surv-row-actions">
                  <Button
                    type="button"
                    variant="primary"
                    size="sm"
                    className="cm-surv-btn"
                    busy={search.isPending}
                    disabled={!canSearch || search.isPending}
                    onClick={onSearch}
                  >
                    Search
                  </Button>
                </div>
              )}
            </Field>
          </div>
        </section>

        {search.isPending ? (
          <StateBlock icon="activity" title="Loading surveillance…">
            Building odds timeline for the selected market.
          </StateBlock>
        ) : null}

        {!searched && graph === undefined && !search.isPending ? (
          <div className="cm-surv-empty">
            <h3>Select filters to view the surveillance graph</h3>
            <p>
              Choose date range, sport, event and market, then Search. Demo:
              Cricket · India v Australia · Match Odds.
            </p>
          </div>
        ) : null}

        {searched && graph && graph.h.length === 0 ? (
          <div className="cm-surv-empty">
            <h3>No data available</h3>
            <p>No runner / bet timeline for this selection.</p>
          </div>
        ) : null}

        {graph && graph.h.length > 0 ? (
          <section className="cm-catalog-card cm-surv-results" aria-label="Results">
            <div className="cm-surv-result-head">
              <div>
                <span className="cm-surv-label">Winner Name</span>
                <strong>{formatSurveillanceWinnerLabel(graph.m.c, graph.m.wn)}</strong>
              </div>
              <div>
                <span className="cm-surv-label">Start time (UTC)</span>
                <strong>
                  {graph.m.mst
                    ? new Date(graph.m.mst).toLocaleString(undefined, {
                        timeZone: 'UTC',
                      })
                    : '—'}
                </strong>
              </div>
            </div>

            {/* Lab golive: Generate Graph dropdown + Select Desired Graph */}
            <div className="cm-surv-graph-tools">
              {canGenerateGraph ? (
              <div className="cm-surv-generate-wrap">
                <Button
                  ref={generateAnchor}
                  type="button"
                  variant="secondary"
                  size="sm"
                  className="cm-surv-btn cm-surv-generate-btn"
                  busy={regenerate.isPending}
                  disabled={!draft.marketId || regenerate.isPending}
                  iconAfter="chevron-down"
                  aria-haspopup="menu"
                  aria-expanded={generateOpen}
                  onClick={() => setGenerateOpen((v) => !v)}
                >
                  Generate Graph
                </Button>
                <Popover
                  open={generateOpen}
                  onClose={() => setGenerateOpen(false)}
                  anchorRef={generateAnchor}
                  label="Generate Graph"
                  width={200}
                >
                  <div className="cm-surv-generate-menu" role="menu">
                    {GENERATE_GRAPH_OPTIONS[mode].map((opt) => (
                      <button
                        key={opt.value}
                        type="button"
                        role="menuitem"
                        className="cm-surv-generate-item"
                        disabled={regenerate.isPending}
                        onClick={() => {
                          setGenerateOpen(false)
                          regenerate.mutate(opt.value)
                        }}
                      >
                        {opt.label}
                      </button>
                    ))}
                  </div>
                </Popover>
              </div>
              ) : null}
              <Field label="Select Desired Graph" className="cm-surv-field-graph-view">
                {({ id }) => (
                  <Select
                    id={id}
                    value={viewGraphType}
                    options={APPLY_GRAPH_VIEW_OPTIONS[mode].map((o) => ({
                      value: o.value,
                      label: o.label,
                    }))}
                    onChange={(e) =>
                      changeGraphTypeView(e.target.value as SurveillanceGraphViewType)
                    }
                  />
                )}
              </Field>
            </div>

            <div className="cm-surv-charts" key={chartEpoch}>
              {graph.h.map((runner) => (
                <RunnerOddsChart key={runner.i} runner={runner} market={graph.m} />
              ))}
            </div>
          </section>
        ) : null}
      </div>
    </CmPanel>
  )
}
